// ABOUTME: email confirmation component that handles token verification
// ABOUTME: confirms email and automatically logs user in after successful confirmation

import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useToast } from "../hooks/useToast";
import { setToken, setUser } from "../services/authSlice";
import { hasRole } from "../utils/roleHelpers";
import stylesAuth from "./AuthPage.module.css";
import styles from "./Form.module.css";

const ConfirmEmailForm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showSuccess, showError } = useToast();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submittingPassword, setSubmittingPassword] = useState(false);

  const token = searchParams.get("token");
  const tenantSlug = searchParams.get("tenantSlug");
  const setupPassword = searchParams.get("setupPassword") === "true";

  const validateTokenOnly = useCallback(async (confirmationToken: string) => {
    const response = await fetch("/api/auth/validate-token", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: confirmationToken }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Invalid confirmation token");
    }

    return response.json();
  }, []);

  const confirmEmail = useCallback(
    async (confirmationToken: string, userPassword?: string) => {
      const body: { token: string; password?: string } = {
        token: confirmationToken,
      };
      if (userPassword) {
        body.password = userPassword;
      }

      const response = await fetch("/api/auth/confirm-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to confirm email");
      }

      return response.json();
    },
    [],
  );

  useEffect(() => {
    if (!token) {
      setError("Invalid or missing confirmation token");
      setLoading(false);
      return;
    }

    const handleTokenValidation = async () => {
      try {
        if (setupPassword) {
          // For password setup flow, validate token first to check if it's valid/not used
          await validateTokenOnly(token);
          // If we get here, token is valid and user needs password setup
          setNeedsPassword(true);
          setLoading(false);
        } else {
          // Normal email confirmation flow
          const data = await confirmEmail(token);

          if (data.token && data.user) {
            // Auto-login after confirmation
            dispatch(setToken(data.token));
            dispatch(setUser(data.user));
            setSuccess(true);
            // Only show success message from backend
            if (data.message) {
              showSuccess(data.message);
            }

            // Redirect to appropriate dashboard based on role and tenantSlug
            setTimeout(() => {
              if (hasRole(data.user.roles, "tenant-owner") && tenantSlug) {
                // Tenant owners go to their tenant orders
                navigate(`/${tenantSlug}/orders`);
              } else if (
                hasRole(data.user.roles, "tenant-owner") &&
                data.user.tenantSlug
              ) {
                // Fallback if tenantSlug not in URL but in user data
                navigate(`/${data.user.tenantSlug}/orders`);
              } else if (
                hasRole(data.user.roles, "developer") ||
                hasRole(data.user.roles, "tenant-manager")
              ) {
                // Developers and tenant managers go to tenant management
                navigate("/tenant-manager/tenants");
              } else {
                // Regular users go to auth page to choose tenant
                navigate("/auth");
              }
            }, 2000);
          } else {
            setSuccess(true);
            if (data.message) {
              showSuccess(data.message);
            }

            // Redirect to login if no auto-login data
            setTimeout(() => {
              navigate("/auth");
            }, 2000);
          }
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to confirm email";
        setError(errorMessage);

        // Error UI component will handle toast and redirect
        setLoading(false);
      }
    };

    handleTokenValidation();
  }, [
    token,
    setupPassword,
    dispatch,
    navigate,
    showSuccess,
    tenantSlug,
    confirmEmail,
    validateTokenOnly,
  ]);

  // Handle error toast and redirect in a separate effect
  useEffect(() => {
    if (error && !needsPassword && !loading) {
      if (error.includes("already been confirmed")) {
        showError(
          "This confirmation link has already been used. Redirecting to login...",
        );
      } else if (
        error.includes("expired") ||
        error.includes("Invalid or expired")
      ) {
        showError(
          "This confirmation link has expired. Please contact support for a new invitation.",
        );
      } else {
        showError(error);
      }

      // Redirect to login after showing error
      const timer = setTimeout(() => {
        navigate("/auth");
      }, 4000);

      return () => clearTimeout(timer);
    }
  }, [error, needsPassword, loading, showError, navigate]);

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!password || password.length < 8) {
      showError("Password must be at least 8 characters long");
      return;
    }

    if (password !== confirmPassword) {
      showError("Passwords do not match");
      return;
    }

    setSubmittingPassword(true);

    try {
      const data = await confirmEmail(token!, password);

      if (data.token && data.user) {
        // Auto-login after confirmation
        dispatch(setToken(data.token));
        dispatch(setUser(data.user));
        setSuccess(true);
        showSuccess("Account setup completed! Welcome to Monulink!");

        // Use same role-based redirect logic as normal confirmation flow
        setTimeout(() => {
          if (hasRole(data.user.roles, "tenant-owner") && tenantSlug) {
            // Tenant owners go to their tenant orders
            navigate(`/${tenantSlug}/orders`);
          } else if (
            hasRole(data.user.roles, "tenant-owner") &&
            data.user.tenantSlug
          ) {
            // Fallback if tenantSlug not in URL but in user data
            navigate(`/${data.user.tenantSlug}/orders`);
          } else if (
            hasRole(data.user.roles, "developer") ||
            hasRole(data.user.roles, "tenant-manager")
          ) {
            // Developers and tenant managers go to tenant management
            navigate("/tenant-manager/tenants");
          } else if (hasRole(data.user.roles, "customer") && tenantSlug) {
            // Customers go to their tenant orders
            navigate(`/${tenantSlug}/orders`);
          } else {
            // Fallback - go to auth page to choose tenant
            navigate("/auth");
          }
        }, 2000);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to setup account";
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setSubmittingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className={stylesAuth.authContainer}>
        <div className={stylesAuth.authCard}>
          <h2 className={stylesAuth.title}>Confirming Your Email</h2>
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <p>Please wait while we confirm your email address...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !needsPassword) {
    return (
      <div className={stylesAuth.authContainer}>
        <div className={stylesAuth.authCard}>
          <h2 className={stylesAuth.title}>Confirmation Error</h2>
          <div style={{ color: "red", textAlign: "center", padding: "1rem" }}>
            {error}
          </div>
          <div
            style={{ textAlign: "center", marginTop: "1rem", color: "#666" }}
          >
            Redirecting to login...
          </div>
        </div>
      </div>
    );
  }

  if (needsPassword && !success) {
    return (
      <div className={stylesAuth.authContainer}>
        <div className={stylesAuth.authCard}>
          <h2 className={stylesAuth.title}>Complete Your Account Setup</h2>
          <p
            style={{ textAlign: "center", marginBottom: "2rem", color: "#666" }}
          >
            Create your password to complete registration
          </p>

          <form onSubmit={handlePasswordSubmit} className={styles.form}>
            <input
              type="password"
              placeholder="Create Password (min 8 characters)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              className={styles.input}
              disabled={submittingPassword}
            />
            <input
              type="password"
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className={styles.input}
              disabled={submittingPassword}
            />

            {error && <div className={styles.error}>{error}</div>}

            <button
              type="submit"
              className={styles.submitBtn}
              disabled={submittingPassword}
            >
              {submittingPassword ? "Setting up Account..." : "Complete Setup"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className={stylesAuth.authContainer}>
        <div className={stylesAuth.authCard}>
          <h2 className={stylesAuth.title}>Setup Complete! 🎉</h2>
          <div style={{ color: "green", textAlign: "center", padding: "1rem" }}>
            Your account has been setup successfully. You're being logged in and
            redirected to your dashboard...
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default ConfirmEmailForm;
